import { useEffect, useMemo, useState } from 'react'
import useMigrateStore from 'src/components/migrate/useMigrateStore'
import {
  ITemplate,
  ITimelineCell,
  TaskCtrlAppCard,
  TemplateCanvas,
} from 'src/interface/org'
import { renderTimelineCanvas } from 'src/util/canvas/board2Canvas'
import { cloneYearWeek } from 'src/util/time'

const convertTemplate = (template: ITemplate): TemplateCanvas => {
  const startWeek = cloneYearWeek(template.startWeek)
  const endWeek = startWeek.add(template.template.workingWeekLength)
  const cells = template.template.rows.map((row) => ({
    column: 0,
    row: 0,
    data: row,
  })) as ITimelineCell[]
  return {
    numberOfDisciplines: template.template.rows.length,
    numberOfWeeks: template.template.workingWeekLength + 1,
    startWeek,
    endWeek,
    pastWeeks: template.pastWeeks,
    cells,
  }
}

export const useTemplateCanvas = (
  templates: ITemplate[],
  selectedTemplateId?: string,
) => {
  const selectedTemplate = useMemo(() => {
    const template = templates.find((t) => t.id === selectedTemplateId)
    if (!template) return undefined
    return { template, convertedTemplate: convertTemplate(template) }
  }, [templates, selectedTemplateId])

  const [canvas, setCanvas] = useState<string>()
  const [loading, setLoading] = useState(false)
  const { canvasPng, setCanvasPng } = useMigrateStore()

  useEffect(() => {
    const render = async () => {
      if (!selectedTemplate?.convertedTemplate || !selectedTemplateId) return
      if (canvasPng?.[selectedTemplateId]) {
        setCanvas(canvasPng?.[selectedTemplateId])
        return
      }
      setLoading(true)
      try {
        const canvasResult = await renderTimelineCanvas(
          selectedTemplate.convertedTemplate,
          (selectedTemplate.template.cards as unknown as TaskCtrlAppCard[]) ??
            [],
          true,
        )
        setCanvasPng(selectedTemplateId, canvasResult ?? '')
        setCanvas(canvasResult)
      } catch (error) {
        console.error('Error rendering timeline canvas:', error)
      } finally {
        setLoading(false)
      }
    }
    render()
  }, [canvasPng, selectedTemplate, selectedTemplateId, setCanvasPng])

  return {
    loading,
    selectedTemplate,
    templateCanvas: canvas,
  }
}
